import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { cn } from "../../utils/common/cn";

/** The "Funded year" chip row used inside a chart panel (same look and behaviour as the Loss Development
 *  chart's). An empty `selected` means every offered year is shown, and every chip reads as pressed. */
export function YearChips<Y extends string | number>({
  years,
  selected,
  onToggle,
  label = "Funded year",
}: {
  years: readonly Y[];
  selected: readonly Y[];
  onToggle: (year: Y) => void;
  label?: string;
}) {
  const active = selected.length ? selected : years;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {years.map((year) => (
        <button
          key={year}
          type="button"
          onClick={() => onToggle(year)}
          aria-pressed={active.includes(year)}
          className={cn(
            "border px-2 py-1 text-xs font-medium tabular-nums transition-colors",
            active.includes(year)
              ? "border-primary bg-primary/15 text-foreground"
              : "border-border text-muted-foreground hover:text-foreground",
          )}
        >
          {year}
        </button>
      ))}
    </div>
  );
}

/** A single-select dropdown sized to sit in a chart panel's header, next to the Realized / Projected toggle. */
export function IndustrySelect({
  value,
  options,
  onChange,
  ariaLabel,
  className = "w-[220px]",
}: {
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
  ariaLabel: string;
  /** Width of the dropdown (default 220px); narrower where two sit in a chart header next to its title. */
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={ariaLabel} className={`h-9 border-border bg-secondary text-sm ${className}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
